import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, updateDoc, collection, onSnapshot, query, where } from 'firebase/firestore';
import { can } from './roles';

const S = {
  page: { padding: '0 24px 24px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px' },
  title: { fontSize: 20, fontWeight: 900, color: '#1A1A2E' },
  sub: { fontSize: 12, color: '#9B8F7A', marginTop: 2 },
  card: { background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 12, padding: 20 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  badge: (color) => ({ background: color + '22', color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' }),
  mono: { fontFamily: 'monospace', fontSize: 11, color: '#6B6055', wordBreak: 'break-all' },
  siteHdr: { fontSize: 13, fontWeight: 800, color: '#1A1A2E', margin: '20px 0 10px', display: 'flex', alignItems: 'center', gap: 8 },
  btn: (color) => ({ background: color, color: '#fff', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700 }),
};

const fmt = (ts) => { try { const d = ts?.toDate?.() || new Date(ts); return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

// Read-only checkpoint register per site. Supervisors set checkpoints up in the
// field (officer app); this view lets a supervisor+ see what exists before
// building routes on top of them. The only mutation here is activate/deactivate
// — no create/edit UI, by design (Phase 1).
export default function CheckpointsPage({ tenantId, userRole }) {
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const canManage = can(userRole, 'manageCheckpoints');

  useEffect(() => {
    if (!tenantId) return;
    const q = query(collection(db, 'checkpoints'), where('tenant_id', '==', tenantId));
    return onSnapshot(q, (s) => {
      setCheckpoints(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [tenantId]);

  const toggleActive = async (cp) => {
    await updateDoc(doc(db, 'checkpoints', cp.id), { active: cp.active === false ? true : false });
  };

  // Group by site, sorted; checkpoints within a site sorted by name.
  const bySite = {};
  for (const cp of checkpoints) {
    const site = (cp.site || 'Unassigned site').trim() || 'Unassigned site';
    (bySite[site] = bySite[site] || []).push(cp);
  }
  const sites = Object.keys(bySite).sort((a, b) => a.localeCompare(b));
  for (const site of sites) bySite[site].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const activeCount = checkpoints.filter((c) => c.active !== false).length;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Checkpoints</div>
          <div style={S.sub}>Registered NFC checkpoints per site — set up in the field before building patrol routes</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {[['Checkpoints', checkpoints.length, '#1A1A2E'], ['Active', activeCount, '#2E7D52'], ['Sites', sites.length, '#C9A84C']].map(([l, v, c]) => (
          <div key={l} style={{ ...S.card, borderLeft: `3px solid ${c}`, padding: '14px 16px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, marginTop: 3 }}>{l}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9B8F7A' }}>Loading checkpoints…</div>
      ) : checkpoints.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#9B8F7A' }}>
          No checkpoints registered yet. A supervisor registers them in the officer app by tapping each NFC tag.
        </div>
      ) : (
        sites.map((site) => (
          <div key={site}>
            <div style={S.siteHdr}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#C9A84C' }} />
              {site}
              <span style={{ fontSize: 11, color: '#9B8F7A', fontWeight: 400 }}>({bySite[site].length})</span>
            </div>
            <div style={S.grid}>
              {bySite[site].map((cp) => {
                const isActive = cp.active !== false;
                return (
                  <div key={cp.id} style={{ ...S.card, borderLeft: `3px solid ${isActive ? '#2E7D52' : '#9B8F7A'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1A1A2E' }}>{cp.name || 'Unnamed checkpoint'}</div>
                      <span style={S.badge(isActive ? '#2E7D52' : '#9B8F7A')}>{isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: '#9B8F7A', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>Tag UID</div>
                      <div style={S.mono}>{cp.nfc_uid || '—'}</div>
                    </div>
                    {(cp.gps_lat != null && cp.gps_lng != null) && (
                      <div style={{ fontSize: 11, color: '#6B6055', marginBottom: 8 }}>
                        📍 {Number(cp.gps_lat).toFixed(5)}, {Number(cp.gps_lng).toFixed(5)}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#9B8F7A' }}>
                      {cp.created_by_email ? `By ${cp.created_by_email}` : ''}{cp.created_at ? ` · ${fmt(cp.created_at)}` : ''}
                    </div>
                    {canManage && (
                      <div style={{ marginTop: 12 }}>
                        <button style={S.btn(isActive ? '#C9A84C' : '#2E7D52')} onClick={() => toggleActive(cp)}>
                          {isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
